import React, {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import Icon from './Icon';
import { cx, useClickOutside } from './primitives';

/**
 * Dropdown menu: a button that opens a list of actions.
 *
 *   <Menu label="Row actions" icon="moreH">
 *     <MenuItem icon="edit" onSelect={edit}>Edit</MenuItem>
 *     <MenuSeparator />
 *     <MenuItem icon="trash" danger onSelect={remove}>Delete</MenuItem>
 *   </Menu>
 *
 * The panel is `position: fixed`, placed from the trigger's rect, because the
 * places menus live (table rows, card headers) clip absolutely positioned
 * children: a scrolling table container would cut the panel off at its edge.
 * It closes on scroll and resize rather than chasing the trigger, which is
 * what native menus do and what keeps it from drifting over the wrong row.
 *
 * Keyboard: Enter/Space/ArrowDown open and focus the first item; arrows move;
 * Home/End jump; Escape closes and returns focus to the trigger; Tab closes.
 *
 * Props: label (accessible name, and the visible text unless `icon` only),
 * icon, buttonClassName, placement ('bottom-end' | 'bottom-start' | 'top-start'
 * | 'top-end' | 'right-end'), matchWidth, trigger (custom trigger content).
 */
const MenuCtx = createContext(null);

function place(rect, placement, panel) {
  const gap = 4;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const h = panel ? panel.offsetHeight : 0;
  const w = panel ? panel.offsetWidth : 0;
  let [side, align] = placement.split('-');
  if (side === 'bottom' && rect.bottom + gap + h > vh - 8 && rect.top - gap - h > 8) side = 'top';
  if (side === 'top' && rect.top - gap - h < 8 && rect.bottom + gap + h < vh - 8) side = 'bottom';
  const style = {};
  if (side === 'right') {
    style.left = Math.min(rect.right + 8, vw - w - 8);
    style.top = Math.max(8, Math.min(rect.bottom - h, vh - h - 8));
    return style;
  }
  if (side === 'bottom') style.top = rect.bottom + gap;
  else style.top = Math.max(8, rect.top - gap - h);
  if (align === 'start') style.left = Math.max(8, Math.min(rect.left, vw - w - 8));
  else style.left = Math.max(8, Math.min(rect.right - w, vw - w - 8));
  return style;
}

export default function Menu({
  label, icon, children, buttonClassName = 'btn btn-ghost btn-icon btn-sm',
  placement = 'bottom-end', matchWidth = false, trigger, menuClassName, disabled,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setPos(null);
    if (refocus) btnRef.current?.focus();
  }, []);

  useClickOutside([btnRef, panelRef], () => close(false), open);

  // Measure after render so the flip logic knows the real panel size.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const next = place(rect, placement, panelRef.current);
    if (matchWidth) next.minWidth = rect.width;
    setPos(next);
  }, [open, placement, matchWidth]);

  useEffect(() => {
    if (!open) return undefined;
    const onScroll = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      close(false);
    };
    const onResize = () => close(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  // Focus the first item once placed.
  useEffect(() => {
    if (open && pos) {
      panelRef.current?.querySelector('[role="menuitem"]:not([disabled])')?.focus();
    }
  }, [open, pos]);

  const items = () => Array.from(panelRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || []);

  const onPanelKeyDown = (e) => {
    const list = items();
    const i = list.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); list[(i + 1) % list.length]?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); list[(i - 1 + list.length) % list.length]?.focus(); }
    else if (e.key === 'Home') { e.preventDefault(); list[0]?.focus(); }
    else if (e.key === 'End') { e.preventDefault(); list[list.length - 1]?.focus(); }
    else if (e.key === 'Escape') { e.preventDefault(); close(true); }
    else if (e.key === 'Tab') { close(false); }
  };

  const onButtonKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <MenuCtx.Provider value={close}>
      <button
        ref={btnRef}
        type="button"
        className={buttonClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={icon && !trigger ? label : undefined}
        title={icon && !trigger ? label : undefined}
        disabled={disabled}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={onButtonKeyDown}
      >
        {trigger || (icon ? <Icon name={icon} /> : label)}
      </button>
      {open && (
        <div
          ref={panelRef}
          role="menu"
          aria-label={label}
          className={cx('menu', menuClassName)}
          style={{ position: 'fixed', visibility: pos ? 'visible' : 'hidden', ...(pos || { top: 0, left: 0 }) }}
          onKeyDown={onPanelKeyDown}
        >
          {children}
        </div>
      )}
    </MenuCtx.Provider>
  );
}

export function MenuItem({ icon, danger, disabled, onSelect, end, children, href, to, as }) {
  const close = useContext(MenuCtx);
  const run = (e) => {
    if (disabled) return;
    // Focus goes back to the trigger BEFORE the action runs. An action that opens
    // a dialog then records the trigger as the place to return focus to; closing
    // without it left focus on a menu item that no longer exists, and the dialog
    // handed it back to <body>.
    close?.(true);
    onSelect?.(e);
  };
  const cls = cx('menu-item', danger && 'danger');
  const content = (
    <>
      {icon && <Icon name={icon} />}
      <span className="grow">{children}</span>
      {end && <span className="menu-item-end">{end}</span>}
    </>
  );
  if (as) {
    const As = as;
    return <As role="menuitem" tabIndex={-1} className={cls} to={to} href={href} onClick={run}>{content}</As>;
  }
  return (
    <button type="button" role="menuitem" tabIndex={-1} className={cls} disabled={disabled} onClick={run}>
      {content}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="menu-separator" role="separator" />;
}

export function MenuLabel({ children }) {
  return <div className="menu-label" role="presentation">{children}</div>;
}
